import { OrderBy } from '@/utils'
import { Trans, useTranslation } from 'react-i18next'
import { useAppSelector, useAppDispatch } from '@/redux/hooks'
import {
  OrderingValues,
  setReturnRateTableArgs
} from '@/redux/api/analytics-api'
import { Base, ReverLoading } from '@itsrever/design-system'
import { Link } from 'react-router'
import React from 'react'
import { ProductReturnRate } from '@/domain/products'
import { NoData } from '@/ui/shared'
import { RateCell, TOOLTIP_TAGS } from '../../rate-table'

const { Card, InfoTooltip, Table, createColumnHelper } = Base

const PAGE_SIZE = 20

const columnHelper = createColumnHelper<ProductReturnRate>()

interface ReturnRateTableProps {
  entries: ProductReturnRate[]
  totalEntries: number
  loading?: boolean
}

const ReturnRateTable: React.FC<ReturnRateTableProps> = ({
  entries,
  totalEntries,
  loading
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const returnRateTableArgs = useAppSelector(
    (store) => store.analyticsApi.returnRateTableArgs
  )
  const orderingValue = returnRateTableArgs.orderingValue ?? OrderingValues.Sold
  const orderingDirection =
    returnRateTableArgs.orderingDirection ?? OrderBy.Desc

  const columns = [
    columnHelper.accessor('name', {
      header: t('return_rate_table.product_name'),
      enableSorting: false
    }),
    columnHelper.accessor('id', {
      header: t('return_rate_table.product_id'),
      enableSorting: false,
      cell: ({ row }) => (
        <>
          <Link
            to={`/analytics/products/${row.original.id}`}
            className="underline"
          >
            {row.original.id}
          </Link>
          {row.original.reference ? ` / ${row.original.reference}` : null}
        </>
      )
    }),
    columnHelper.accessor('totalPurchases', {
      id: OrderingValues.Sold,
      header: t('return_rate_table.items_purchased'),
      meta: {
        headInfo: (
          <InfoTooltip
            label={t('return_rate_table.about_items_purchased')}
            content={t('return_rate_table.purchased_description')}
          />
        )
      }
    }),
    columnHelper.accessor('totalReturns', {
      id: OrderingValues.Returned,
      header: t('return_rate_table.items_returned'),
      meta: {
        headInfo: (
          <InfoTooltip
            label={t('return_rate_table.about_items_returned')}
            content={t('return_rate_table.returned_description')}
          />
        )
      }
    }),
    columnHelper.accessor('returnRate', {
      id: OrderingValues.ReturnRate,
      header: t('return_rate_table.return_rate'),
      cell: ({ getValue }) => <RateCell value={getValue()} />,
      meta: {
        headInfo: (
          <InfoTooltip
            label={t('return_rate_table.about_return_rate')}
            content={
              <Trans
                i18nKey="return_rate_table.return_rate_description"
                components={TOOLTIP_TAGS}
              />
            }
          />
        )
      }
    })
  ]

  return (
    <Card className="my-4 h-auto overflow-hidden">
      {loading ? (
        <div className="p-4">
          <ReverLoading />
        </div>
      ) : totalEntries > 0 ? (
        <Table
          data={entries}
          columns={columns}
          // The API sorts and pages the products.
          manualSorting
          sorting={[
            { id: orderingValue, desc: orderingDirection === OrderBy.Desc }
          ]}
          onSortingChange={([sort]) => {
            if (!sort) return
            dispatch(
              setReturnRateTableArgs({
                ...returnRateTableArgs,
                orderingValue: sort.id,
                orderingDirection: sort.desc ? OrderBy.Desc : OrderBy.Asc
              })
            )
          }}
          pagination={{
            pageIndex: (returnRateTableArgs.pageNumber ?? 1) - 1,
            pageSize: PAGE_SIZE,
            pageCount: Math.ceil(totalEntries / PAGE_SIZE),
            onPageChange: (index) =>
              dispatch(
                setReturnRateTableArgs({
                  ...returnRateTableArgs,
                  pageNumber: index + 1
                })
              ),
            previousLabel: t('return_rate_table.previous_page'),
            nextLabel: t('return_rate_table.next_page')
          }}
        />
      ) : (
        <NoData />
      )}
    </Card>
  )
}

export default ReturnRateTable
